import { useState, useRef, useCallback } from "react";
import { CloudArrowUp, File as FileIcon, X, Spinner } from "@phosphor-icons/react";
import { cn } from "../lib/utils";
import API from "../lib/api";
import { openAuthFile } from "../lib/fileOpen";
import AuthImage from "./AuthImage";

const ACCEPT_MAP = {
  image: "image/*",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt",
  all: "image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt",
};

export function FileDropZone({
  files = [],
  onFilesChange,
  maxFiles = 10,
  maxSizeMB = 20,
  accept = "all",
  folder = "uploads",
  compact = false,
  className,
  renderBadge,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const processFiles = useCallback(
    async (incoming) => {
      const remaining = maxFiles - files.length;
      if (remaining <= 0) return;
      const batch = Array.from(incoming).slice(0, remaining);

      setUploading(true);
      const newFiles = [];

      for (let i = 0; i < batch.length; i++) {
        const file = batch[i];
        if (file.size > maxSizeMB * 1024 * 1024) continue;

        const tempId = `${Date.now()}-${i}`;
        setUploadProgress((p) => ({ ...p, [tempId]: 0 }));

        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("folder", folder);

          const { data } = await API.post("/upload", formData, {
            onUploadProgress: (e) => {
              const pct = Math.round((e.loaded * 100) / (e.total || 1));
              setUploadProgress((p) => ({ ...p, [tempId]: pct }));
            },
          });

          newFiles.push({
            name: file.name,
            url: data.file_url,
            type: file.type,
            size: data.size,
            file_id: data.file_id,
          });
        } catch {
          // skip failed
        } finally {
          setUploadProgress((p) => {
            const copy = { ...p };
            delete copy[tempId];
            return copy;
          });
        }
      }

      if (newFiles.length > 0) {
        onFilesChange([...files, ...newFiles]);
      }
      setUploading(false);
    },
    [files, maxFiles, maxSizeMB, folder, onFilesChange]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      processFiles(e.dataTransfer.files);
    },
    [processFiles]
  );

  const handleInputChange = (e) => {
    if (e.target.files?.length) processFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const isImage = (type) => type?.startsWith("image/");
  const activeUploads = Object.keys(uploadProgress).length;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Drop zone */}
      <div
        data-testid="file-drop-zone"
        role="button"
        tabIndex={0}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && !uploading && inputRef.current?.click()}
        className={cn(
          "relative border-2 border-dashed rounded-lg transition-all cursor-pointer group",
          compact ? "p-3" : "p-5",
          dragOver
            ? "border-primary bg-primary/5 scale-[1.01]"
            : "border-border hover:border-primary/50 hover:bg-muted/30",
          uploading && "pointer-events-none opacity-70"
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_MAP[accept] || accept}
          onChange={handleInputChange}
          className="hidden"
          data-testid="file-drop-zone-input"
        />
        <div className={cn("flex flex-col items-center gap-1 text-center", compact && "flex-row gap-2 text-left")}>
          {uploading ? (
            <Spinner size={compact ? 18 : 24} className="animate-spin text-primary" />
          ) : (
            <CloudArrowUp
              size={compact ? 18 : 28}
              weight="duotone"
              className={cn(
                "transition-transform text-muted-foreground group-hover:text-primary",
                dragOver && "scale-110 text-primary"
              )}
            />
          )}
          <div>
            <p className={cn("font-medium", compact ? "text-xs" : "text-sm")}>
              {uploading
                ? `Uploading ${activeUploads} file${activeUploads > 1 ? "s" : ""}...`
                : dragOver
                ? "Drop files here"
                : "Drag & drop files or click to browse"}
            </p>
            {!compact && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Up to {maxFiles} files, {maxSizeMB}MB each
              </p>
            )}
          </div>
        </div>

        {/* Upload progress bars */}
        {activeUploads > 0 && (
          <div className="mt-2 space-y-1">
            {Object.entries(uploadProgress).map(([id, pct]) => (
              <div key={id} className="h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-200"
                  style={{ width: `${pct}%` }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* File previews */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="file-drop-zone-previews">
          {files.map((f, i) => (
            <div
              key={`${f.file_id || f.url}-${i}`}
              role="button"
              tabIndex={0}
              title={`View ${f.name || "file"}`}
              onClick={() => openAuthFile(f.url)}
              onKeyDown={(e) => e.key === "Enter" && openAuthFile(f.url)}
              className="group/file relative border border-border rounded-md overflow-hidden bg-muted/40 hover:border-primary/40 transition-colors cursor-pointer"
              data-testid={`file-preview-${i}`}
            >
              {isImage(f.type) ? (
                <div className="w-20 h-20 relative">
                  <AuthImage
                    path={f.url}
                    alt={f.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover/file:bg-black/20 transition-colors" />
                </div>
              ) : (
                <div className="w-20 h-20 flex flex-col items-center justify-center gap-1 px-1">
                  <FileIcon size={20} weight="duotone" className="text-muted-foreground" />
                  <span className="text-[8px] text-muted-foreground truncate w-full text-center">
                    {f.name?.split(".").pop()?.toUpperCase()}
                  </span>
                </div>
              )}
              <p className="text-[9px] text-muted-foreground truncate px-1.5 py-1 border-t border-border bg-background/80">
                {f.name?.length > 14 ? f.name.slice(0, 11) + "..." : f.name}
              </p>
              {renderBadge && (
                <div className="absolute top-0.5 left-0.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  {renderBadge(f, i)}
                </div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-destructive/90 text-white flex items-center justify-center opacity-0 group-hover/file:opacity-100 transition-opacity"
                data-testid={`file-remove-${i}`}
              >
                <X size={10} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
